// components/EditEntryForm.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';

const EditEntryForm = ({ entry, onSubmit }) => {
  const [formData, setFormData] = useState({ ...entry });
  const [formErrors, setFormErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const countWords = (text) => text.trim().split(/\s+/).filter(Boolean).length;

  const validateForm = () => {
    const errors = {};
    for (const [key, value] of Object.entries(formData)) {
      if (key !== 'openToContact' && key !== 'name' && key !== 'preferredContact' && !value) {
        errors[key] = 'This field is required';
      }
    }

    if (formData.openToContact) {
      if (!formData.name) {
        errors.name = 'Name is required when open to contact';
      }
      if (!formData.contact) {
        errors.preferredContact = 'Preferred contact is required when open to contact';
      }
    }

    if (countWords(formData.interviewProcess) < 20) {
      errors.interviewProcess = 'Minimum 20 words required';
    }
    if (countWords(formData.questionsAsked) < 20) {
      errors.questionsAsked = 'Minimum 20 words required';
    }
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="interview-form">
      <h2>Edit Entry</h2>
      <div className="form-group checkbox-group">
        <label htmlFor="openToContact">
          <input
            id = "openToContact"
            type="checkbox"
            name="openToContact"
            checked={formData.openToContact || false}
            onChange={handleInputChange}
          />
          Open To Contact?
        </label>
      </div>

      {formData.openToContact && (
        <>
        <div className="form-group">
          <label htmlFor="name">Name</label>
          <input
            id = "name"
            type="text"
            name="name"
            value={formData.name || ''}
            onChange={handleInputChange}
            className={formErrors.name ? 'error' : ''}
          />
        </div>
        <div className="form-group">
          <label htmlFor="contact">Contact</label>
          <input
            id = "contact"
            type="contact"
            name="contact"
            value={formData.contact || ''}
            onChange={handleInputChange}
            className={formErrors.preferredContact ? 'error' : ''}
          />
        </div>

      </>
      )}
      <div className="form-group">
        <label htmlFor="yearApplied">Year Applied</label>
        <input
          id = "yearApplied"
          type="text"
          name="yearApplied"
          value={formData.yearApplied || ''}
          onChange={handleInputChange}
          className={formErrors.yearApplied ? 'error' : ''}
        />
      </div>
      <div className="form-group">
        <label htmlFor="gpa">GPA</label>
        <input
          id = "gpa"
          type="text"
          name="gpa"
          value={formData.gpa || ''}
          onChange={handleInputChange}
          className={formErrors.gpa ? 'error' : ''}
        />
      </div>
      <div className="form-group">
        <label htmlFor="companyName">Company Name</label>
        <input
          id = "companyName"
          type="text"
          name="companyName"
          value={formData.companyName || ''}
          onChange={handleInputChange}
          className={formErrors.companyName ? 'error' : ''}
        />
      </div>
      <div className="form-group">
        <label htmlFor="roleTitle">Role Title</label>
        <input
          id = "roleTitle"
          type="text"
          name="roleTitle"
          value={formData.roleTitle || ''}
          onChange={handleInputChange}
          className={formErrors.roleTitle ? 'error' : ''}
        />
      </div>
      <div className="form-group">
        <label htmlFor="positionType">Position Type</label>
        <input
          id = "positionType"
          type="text"
          name="positionType"
          value={formData.positionType || ''}
          onChange={handleInputChange}
          className={formErrors.positionType ? 'error' : ''}
        />
      </div>
      <div className="form-group">
        <label htmlFor="startDate">Start Date</label>
        <input
          id = "startDate"
          type="date"
          name="startDate"
          value={formData.startDate || ''}
          onChange={handleInputChange}
          className={formErrors.startDate ? 'error' : ''}
        />
      </div>
      <div className="form-group">
        <label htmlFor="applicationMethod">Application Method</label>
        <input
          id = "applicationMethod"
          type="text"
          name="applicationMethod"
          value={formData.applicationMethod || ''}
          onChange={handleInputChange}
          className={formErrors.applicationMethod ? 'error' : ''}
        />
      </div>
      <div className="form-group">
        <label htmlFor="hiringProcessLength">Hiring Process Length</label>
        <input
          id = "hiringProcessLength"
          type="text"
          name="hiringProcessLength"
          value={formData.hiringProcessLength || ''}
          onChange={handleInputChange}
          className={formErrors.hiringProcessLength ? 'error' : ''}
        />
      </div>
      <div className="form-group">
        <label htmlFor="interviewProcess">Interview Process</label>
        <textarea
          id = "interviewProcess"
          name="interviewProcess"
          value={formData.interviewProcess || ''}
          onChange={handleInputChange}
          rows="3"
          className={formErrors.interviewProcess ? 'error' : ''}
        />
      </div>
      <div className="form-group">
        <label htmlFor="questionsAsked">Questions Asked</label>
        <textarea
          id = "questionsAsked"
          name="questionsAsked"
          value={formData.questionsAsked || ''}
          onChange={handleInputChange}
          rows="3"
          className={formErrors.questionsAsked ? 'error' : ''}
        />
      </div>
      <div className="form-group">
        <label htmlFor="hourlyRate">Hourly Rate</label>
        <input
          id = "hourlyRate"
          type="text"
          name="hourlyRate"
          value={formData.hourlyRate || ''}
          onChange={handleInputChange}
        />
      </div>
      <div className="form-group">
        <label htmlFor="baseSalary">Base Salary</label>
        <input
          id = "baseSalary"
          type="text"
          name="baseSalary"
          value={formData.baseSalary || ''}
          onChange={handleInputChange}
        />
      </div>
      <div className="form-group">
        <label htmlFor="totalCompensation">Total Compensation</label>
        <input
          id = "totalCompensation"
          type="text"
          name="totalCompensation"
          value={formData.totalCompensation || ''}
          onChange={handleInputChange}
        />
      </div>
      <div className="form-group">
        <label htmlFor="location">Location</label>
        <input
          id = "location"
          type="text"
          name="location"
          value={formData.location || ''}
          onChange={handleInputChange}
          className={formErrors.location ? 'error' : ''}
        />
      </div>

      <button type="submit" className="submit-button">
        Save Changes
      </button>
    </form>
  );
};

EditEntryForm.propTypes = {
  entry: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};


export default EditEntryForm;